import React, { useState, useEffect, useCallback } from 'react';
import { gpuAPI, serversAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './GpuPage.css';

import { GpuServers } from './gpu/GpuServers';
import { GpuInstances, GpuAllocations } from './gpu/GpuAllocations';
import { GpuRequests } from './gpu/GpuRequests';
import { GpuRequestForm } from './gpu/GpuQuotas';
import {
  GpuRegisterModal,
  GpuAllocateModal,
  GpuAllocResultModal,
  GpuTerminalModal,
} from './gpu/GpuInstances';

export default function GpuPage() {
  const { isAdmin, isSuperAdmin, hasSectionAction } = useAuth();
  const gpuCreate = hasSectionAction('gpu', 'create');
  const gpuEdit   = hasSectionAction('gpu', 'edit');
  const gpuDelete = hasSectionAction('gpu', 'delete');
  // GPU Servers / All Allocations are superadmin-only — admins and members only
  // ever see My Instances (which is org-scoped for them; see list_my_allocations).
  const [tab, setTab]           = useState(isSuperAdmin ? 'servers' : 'myinstances');
  const [gpuServers, setGpuServers] = useState([]);
  const [myAllocs, setMyAllocs] = useState([]);
  const [allAllocs, setAllAllocs] = useState([]);
  const [quota, setQuota]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // GPU Requests
  const [gpuRequests, setGpuRequests]         = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm]         = useState({
    project_name: '', officer_name: '', vertical: '', email: '', mobile: '',
    project_description: '', os_version: 'Ubuntu 22.04 LTS',
    ports_required: '22,443', internet_access: true, access_type: 'ssh',
    gpu_usage_type: 'fine-tuning', gpu_memory_gb: 80, gpu_count: 1,
    vcpu_requested: 48, ram_requested_gb: 196, storage_tb: 4,
    nvme_storage_tb: 2,
    frameworks: [], databases: [], extra_packages: '', duration_days: 30,
    llm_model: '', llm_quantization: 'int8',
    subdomain: '', app_port: '', extra_ports: '',
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestError, setRequestError]           = useState('');
  const [requestSuccess, setRequestSuccess]       = useState('');
  const [provisioningReqId, setProvisioningReqId] = useState(null);
  const [provisionResult, setProvisionResult]     = useState(null);
  const [podStatus, setPodStatus]                 = useState({});

  // Web terminal
  const [terminalAlloc, setTerminalAlloc]   = useState(null);
  const [termInput, setTermInput]           = useState('');
  const [termHistories, setTermHistories]   = useState({});  // { [allocId]: [{type, text}] }
  const [termRunning, setTermRunning]       = useState(false);

  // Register modal
  const [showRegister, setShowRegister] = useState(false);
  const [servers, setServers]   = useState([]);
  const [selectedServerId, setSelectedServerId] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState('');

  // Allocate modal
  const [allocModal, setAllocModal] = useState(null); // {gpuServerId, instanceId, instanceName, vramGb}
  const [allocImage, setAllocImage] = useState('pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime');
  const [allocHours, setAllocHours] = useState(24);
  const [allocating, setAllocating] = useState(false);
  const [allocResult, setAllocResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [gsRes, maRes, qRes] = await Promise.all([
        gpuAPI.listServers(),
        gpuAPI.myAllocations(),
        gpuAPI.getQuota(),
      ]);
      setGpuServers(gsRes.data);
      setMyAllocs((maRes.data || []).filter(a => !a.workload_type || a.workload_type === 'gpu'));
      setQuota(qRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load GPU data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmin = useCallback(async () => {
    try {
      const res = await gpuAPI.allAllocations();
      setAllAllocs(res.data);
    } catch (e) {}
  }, []);

  const loadRequests = useCallback(async () => {
    try { const r = await gpuAPI.listRequests(); setGpuRequests(r.data); } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'admin') loadAdmin(); }, [tab, loadAdmin]);
  useEffect(() => { if (tab === 'requests') loadRequests(); }, [tab, loadRequests]);

  const handleSubmitRequest = async () => {
    if (!requestForm.project_name) { setRequestError('Project name is required'); return; }
    setSubmittingRequest(true); setRequestError(''); setRequestSuccess('');
    try {
      await gpuAPI.submitRequest(requestForm);
      setRequestSuccess('Request submitted successfully! An admin will review and provision your GPU instance.');
      setShowRequestForm(false);
      setRequestForm({
        project_name: '', officer_name: '', vertical: '', email: '', mobile: '',
        project_description: '', os_version: 'Ubuntu 22.04 LTS', ports_required: '22,443',
        internet_access: true, access_type: 'ssh', gpu_usage_type: 'fine-tuning',
        gpu_memory_gb: 80, gpu_count: 1, vcpu_requested: 48, ram_requested_gb: 196,
        storage_tb: 4, nvme_storage_tb: 2, frameworks: [], databases: [],
        extra_packages: '', duration_days: 30, llm_model: '', llm_quantization: 'int8',
        subdomain: '', app_port: '', extra_ports: '',
      });
      loadRequests();
    } catch (e) {
      setRequestError(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleApproveRequest = async (reqId, status) => {
    try {
      await gpuAPI.updateRequest(reqId, { status });
      loadRequests();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update request'); }
  };

  const appendTerm = (allocId, entry) =>
    setTermHistories(h => ({ ...h, [allocId]: [...(h[allocId] || []), entry] }));

  const handleTermExec = async (e) => {
    e.preventDefault();
    if (!termInput.trim() || !terminalAlloc) return;
    const cmd = termInput.trim();
    const allocId = terminalAlloc.id;
    appendTerm(allocId, { type: 'input', text: `$ ${cmd}` });
    setTermInput('');
    setTermRunning(true);
    try {
      const res = await gpuAPI.execInAllocation(allocId, cmd);
      const out = res.data.stdout || '';
      const err = res.data.stderr || '';
      if (out) appendTerm(allocId, { type: 'output', text: out });
      if (err) appendTerm(allocId, { type: 'error', text: err });
    } catch (e) {
      appendTerm(allocId, { type: 'error', text: e.response?.data?.detail || 'Command failed' });
    } finally {
      setTermRunning(false);
    }
  };

  const handleCheckPodStatus = async (reqId) => {
    try {
      const res = await gpuAPI.getPodStatus(reqId);
      setPodStatus(s => ({...s, [reqId]: res.data}));
    } catch (e) {
      setPodStatus(s => ({...s, [reqId]: {status: 'error', logs: e.response?.data?.detail || 'Failed'}}));
    }
  };

  const handleProvisionRequest = async (reqId, gpuServerId) => {
    setProvisioningReqId(reqId); setProvisionResult(null);
    try {
      const res = await gpuAPI.provisionRequest(reqId, { gpu_server_id: gpuServerId });
      setProvisionResult(res.data);
      loadRequests(); load();
    } catch (e) {
      setProvisionResult({ error: e.response?.data?.detail || 'Provisioning failed' });
    } finally {
      setProvisioningReqId(null);
    }
  };

  const handleReProvision = async (reqId) => {
    try {
      await gpuAPI.updateRequest(reqId, { status: 'approved' });
      loadRequests();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const openRegister = async () => {
    setShowRegister(true); setRegisterError('');
    const res = await serversAPI.list().catch(() => ({ data: [] }));
    const registered_ids = gpuServers.map(g => g.server_id);
    setServers(res.data.filter(s => !registered_ids.includes(s.id)));
    setSelectedServerId('');
  };

  const handleRegister = async () => {
    if (!selectedServerId) { setRegisterError('Select a server'); return; }
    setRegistering(true); setRegisterError('');
    try {
      await gpuAPI.registerServer({ server_id: parseInt(selectedServerId) });
      setShowRegister(false);
      load();
    } catch (e) {
      setRegisterError(e.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleAllocate = async () => {
    if (!allocModal) return;
    setAllocating(true);
    try {
      const res = await gpuAPI.allocate(allocModal.gpuServerId, allocModal.instanceId, {
        docker_image: allocImage, expires_hours: allocHours
      });
      setAllocResult(res.data);
      load();
    } catch (e) {
      setAllocResult({ error: e.response?.data?.detail || 'Allocation failed' });
    } finally {
      setAllocating(false);
    }
  };

  // skipReload: during Live Monitor polling we refresh many servers back-to-back;
  // reloading the whole page after each one hammered the API. Let the caller
  // reload ONCE after the batch. Returns the promise so callers can sequence.
  const handleRefresh = async (gpuServerId, skipReload = false) => {
    try {
      // gpuServerId=null is a "reload only" signal (used once per Live Monitor
      // cycle) — don't hit the refresh API with a null id.
      if (gpuServerId != null) await gpuAPI.refreshMetrics(gpuServerId);
      if (!skipReload) load();
    } catch (e) { /* swallow — a single server's metrics failing must not break the loop */ }
  };

  const handleTerminate = async (allocId) => {
    const confirmed = window.confirm(
      '⚠️ TERMINATE GPU INSTANCE?\n\n' +
      'This will permanently:\n' +
      '• Delete the K8s pod and all running processes\n' +
      '• Wipe all data inside the container\n' +
      '• Release the GPU back to the pool\n\n' +
      '❗ Data stored in /opt/dlami/nvme (NVMe) will be PRESERVED on the host.\n' +
      '❗ Data stored inside the container will be PERMANENTLY LOST.\n\n' +
      'Type OK to confirm termination.'
    );
    if (!confirmed) return;
    try { await gpuAPI.terminateAlloc(allocId); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed to terminate'); }
  };

  const handleStop = async (allocId) => {
    try { await gpuAPI.stopAllocation(allocId); load(); } catch (e) {}
  };

  const handleStart = async (allocId) => {
    try { await gpuAPI.startAllocation(allocId); load(); } catch (e) {}
  };

  const handleReleaseStuck = async (gpuServerId) => {
    const r = await gpuAPI.releaseStuck(gpuServerId).catch(e => ({ data: { error: e.response?.data?.detail } }));
    if (r.data.count > 0) { alert(`Released ${r.data.count} stuck instance(s)`); load(); }
    else alert('No stuck instances found');
  };

  const handleDeleteServer = async (gpuServerId) => {
    if (window.confirm('Remove GPU server registration?')) {
      await gpuAPI.deleteServer(gpuServerId);
      load();
    }
  };

  return (
    <div className="gpu-page">
      <div className="gpu-header">
        <div>
          <h1>🖥 GPU Servers</h1>
          <p className="gpu-subtitle">Manage GPU resources and user instances</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {quota && (
            <div className="gpu-quota-badge">
              <span>{myAllocs.filter(a => a.status==='running').length} / {quota.max_instances} instances</span>
            </div>
          )}
          {/* Request/approval flow removed — use direct Launch (Compute → Instances → Launch GPU Instance) */}
          {/* GPU server registration is a platform-level concern — superadmin only. */}
          {isSuperAdmin && (
            <button className="button-primary" onClick={openRegister}>+ Register GPU Server</button>
          )}
          <button className="button-secondary" onClick={load} disabled={loading}>⟳ Refresh</button>
        </div>
      </div>

      {error && <div className="resource-error">{error}</div>}
      {requestSuccess && (
        <div style={{ background: '#f0fff4', color: '#276749', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {requestSuccess}
        </div>
      )}

      {/* Tabs */}
      <div className="gpu-tabs">
        {(isSuperAdmin
          ? [['servers','GPU Servers'], ['myinstances','My Instances'], ['admin','All Allocations']]
          : [['myinstances','My Instances']]
        ).map(([id,label]) => (
          <button key={id} className={`gpu-tab ${tab===id?'active':''}`} onClick={() => setTab(id)}>
            {label}{id==='requests' && gpuRequests.filter(r=>r.status==='pending').length > 0 &&
              <span style={{ background: '#e53e3e', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, marginLeft: 6 }}>
                {gpuRequests.filter(r=>r.status==='pending').length}
              </span>}
          </button>
        ))}
      </div>

      {tab === 'servers' && (
        <GpuServers
          gpuServers={gpuServers}
          loading={loading}
          onRefresh={handleRefresh}
          onRegister={openRegister}
          onDelete={handleDeleteServer}
          onReleaseStuck={handleReleaseStuck}
          onAllocate={setAllocModal}
          isAdmin={isAdmin}
          canCreate={gpuCreate}
          canEdit={gpuEdit}
          canDelete={gpuDelete}
        />
      )}

      {tab === 'myinstances' && (
        <GpuInstances
          myAllocs={myAllocs}
          onStop={handleStop}
          onStart={handleStart}
          onTerminate={handleTerminate}
          onOpenTerminal={(a) => { setTerminalAlloc(a); }}
          onRefresh={load}
          canEdit={gpuEdit}
          canDelete={gpuDelete}
        />
      )}

      {tab === 'admin' && (
        <GpuAllocations
          allAllocs={allAllocs}
          onTerminate={handleTerminate}
        />
      )}

      {tab === 'requests' && (
        <GpuRequests
          gpuRequests={gpuRequests}
          gpuServers={gpuServers}
          provisionResult={provisionResult}
          provisioningReqId={provisioningReqId}
          podStatus={podStatus}
          onNewRequest={() => { setShowRequestForm(true); setRequestError(''); }}
          onApproveRequest={handleApproveRequest}
          onProvisionRequest={handleProvisionRequest}
          onCheckPodStatus={handleCheckPodStatus}
          onReProvision={handleReProvision}
          onDismissProvisionResult={() => setProvisionResult(null)}
          isAdmin={isAdmin}
          canCreate={gpuCreate}
          canEdit={gpuEdit}
        />
      )}

      {/* Modals */}
      <GpuRequestForm
        show={showRequestForm}
        requestForm={requestForm}
        setRequestForm={setRequestForm}
        submittingRequest={submittingRequest}
        requestError={requestError}
        onSubmit={handleSubmitRequest}
        onClose={() => setShowRequestForm(false)}
      />

      <GpuTerminalModal
        terminalAlloc={terminalAlloc}
        termHistory={terminalAlloc ? (termHistories[terminalAlloc.id] || []) : []}
        termInput={termInput}
        setTermInput={setTermInput}
        termRunning={termRunning}
        onExec={handleTermExec}
        onClose={() => setTerminalAlloc(null)}
      />

      <GpuRegisterModal
        show={showRegister}
        servers={servers}
        selectedServerId={selectedServerId}
        setSelectedServerId={setSelectedServerId}
        registering={registering}
        registerError={registerError}
        onRegister={handleRegister}
        onClose={() => setShowRegister(false)}
      />

      {!allocResult && (
        <GpuAllocateModal
          allocModal={allocModal}
          allocImage={allocImage}
          setAllocImage={setAllocImage}
          allocHours={allocHours}
          setAllocHours={setAllocHours}
          allocating={allocating}
          onAllocate={handleAllocate}
          onClose={() => setAllocModal(null)}
        />
      )}

      <GpuAllocResultModal
        allocResult={allocResult}
        onClose={() => { setAllocResult(null); setAllocModal(null); setTab('myinstances'); }}
      />
    </div>
  );
}
